/** Glossary · 192 Block 1 entries (60 curated + 132 sprint_06 master) +
 *  68 Block 2 entries (S07 BUG · S08 SQL via writer slot 4 MAY) =
 *  260 terms total · per ASK-3.4-4=A pragmatic extension; Slot J Pass 2
 *  master collation refines later via additive edits.
 */

export interface GlossaryTerm {
  id: string;
  termRu: string;
  termEn: string;
  short: string;
  extended?: string;
  istqb?: string;
  related?: string[];
  introducedIn: string;
  /** slot_s06_rework (11 JUN 2026) · «Жизнь vs экзамен»: чем канон ISTQB
   *  отличается от того, как термин живёт на практике/в курсе. Рендерится
   *  бейджем «осторожно: канон» на обороте карточки тренажёра; термины с этим
   *  полем собираются в отдельную колоду. Стартовый набор выверен по
   *  официальным источникам (03_SOURCES/istqb/) · полный минимум доливает
   *  слот ISTQB_CANON_PACK. */
  examTrap?: string;
  /** slot ISTQB_CANON_PACK (12 JUN 2026) · когнитивный уровень термина на
   *  экзамене CTFL 4.0 (K1 запомнить · K2 понять · K3 применить). Рендерится
   *  чипом на карточке тренажёра — подсказывает: зубрить определение (K1),
   *  понимать смысл (K2) или уметь применить технику на задаче (K3).
   *  Размечаются экзамен-критичные термины (istqb-тег). */
  klevel?: 'K1' | 'K2' | 'K3';
}

// Block 2 terms (S07 BUG + S08 SQL · ~68 terms) imported from a separate file
// to keep this main array readable. Per ASK-3.4-4=A · 3.4a extends directly.
import { BLOCK_2_TERMS } from './glossary-block-2';
// Block 3 terms (S13 · Automation taster) — отдельный файл, вне CTFL.
import { BLOCK_3_TERMS, BLOCK_3_MOBILE_TERMS } from './glossary-block-3';

/**
 * Block 1 terms (S01-S05 · фундамент CTFL Foundation 4.0). Это именно тот набор,
 * который студент проходит к спринту S06 — на нём строится CTFL-флешкарт-тренажёр
 * (Flashcards.tsx). Block 2 (S07+: Bug Lifecycle / SQL и т.д.) ещё НЕ пройден на
 * S06, поэтому в тренажёр не попадает — но входит в общий GLOSSARY ниже, который
 * питает тултипы и поиск по всему курсу.
 */
export const BLOCK_1_TERMS: ReadonlyArray<GlossaryTerm> = [
  {
    id: 'qa',
    termRu: 'Quality Assurance (QA)',
    termEn: 'Quality Assurance',
    short: 'Процесс предотвращения дефектов через ревью требований, риск-анализ, обсуждение AC до старта.',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['qc', 'static-testing'],
    examTrap:
      'В вакансиях «QA» = тестировщик. На экзамене QA — процессный подход к КАЧЕСТВУ в целом (предотвращение), тестирование — деятельность внутри QC (обнаружение). Вопрос «testing is part of…» ждёт ответа QC, не наоборот.',
  },
  {
    id: 'qc',
    termRu: 'Quality Control (QC)',
    termEn: 'Quality Control',
    short: 'Контроль продукта — выполнение тестов после написания кода для нахождения дефектов.',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['qa', 'dynamic-testing'],
    examTrap:
      'На практике «QA-инженер» = тестировщик, а тестирование зовут «QA». Канон строго наоборот: тестирование — деятельность внутри QC (контроль продукта, обнаружение дефектов после написания кода), а QA — процессный подход к качеству в целом (предотвращение). Вопрос «тестирование — часть чего?» ждёт QC.',
  },
  {
    id: 'static-testing',
    termRu: 'Статическое тестирование',
    termEn: 'Static Testing',
    short: 'Поиск дефектов в артефактах (требования, код, тест-кейсы) без их выполнения.',
    extended: 'Включает review, walkthrough, technical review, inspection.',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    introducedIn: 'S01',
    related: ['inspection', 'walkthrough', 'review'],
    examTrap:
      'Многие считают «тестирование = запустить и проверить». Канон: статическое тестирование НЕ выполняет код — ревью и статический анализ находят ДЕФЕКТЫ прямо в артефактах (требования, код, тест-кейсы), а не сбои (failures). Дистрактор «находит сбои» ложный: сбой возможен только при выполнении.',
  },
  {
    id: 'dynamic-testing',
    termRu: 'Динамическое тестирование',
    termEn: 'Dynamic Testing',
    short: 'Тестирование с выполнением кода. Включает unit, integration, system, acceptance.',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    introducedIn: 'S01',
    examTrap:
      '«Динамическое» звучит как нечто продвинутое. Канон проще: это любое тестирование с ВЫПОЛНЕНИЕМ кода — оно обнаруживает сбои (failures), по которым уже ищут дефект. Парная ловушка к static-testing: статика ловит дефекты без запуска, динамика проявляет сбои запуском.',
  },
  {
    id: 'severity',
    termRu: 'Severity',
    termEn: 'Severity',
    short: 'Объективная мера влияния дефекта на продукт. Ставит QA. Шкала S1-S4.',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['priority'],
    examTrap:
      'В жизни severity и priority часто сливаются в одно поле трекера. На экзамене различай строго: severity — техническое влияние на систему (оценивает тестировщик), priority — бизнес-срочность исправления. Critical-дефект может иметь низкий priority и наоборот.',
  },
  {
    id: 'priority',
    termRu: 'Priority',
    termEn: 'Priority',
    short: 'Субъективная мера срочности исправления. Ставит PM. Может отличаться от Severity.',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['severity'],
    examTrap:
      'В жизни severity и priority часто смешивают. Канон разводит: severity — насколько серьёзен сбой для системы (тех. влияние), priority — насколько срочно чинить (бизнес-очередь). Косметический баг на главной может быть low severity + high priority. Они независимы — вопрос проверяет, что вы их не приравниваете.',
  },
  {
    id: 'invest',
    termRu: 'INVEST',
    termEn: 'INVEST',
    short: 'Independent · Negotiable · Valuable · Estimable · Small · Testable — критерии хорошей user story.',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    introducedIn: 'S02',
    related: ['user-story'],
  },
  {
    id: 'rtm',
    termRu: 'Requirements Traceability Matrix',
    termEn: 'Requirements Traceability Matrix (RTM)',
    short: 'Таблица «требования × тест-кейсы» для покрытия. Forward / backward traceability.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S02',
  },
  {
    id: 'ep',
    termRu: 'Equivalence Partitioning',
    termEn: 'Equivalence Partitioning (EP)',
    short: 'Деление входных данных на классы с одинаковым поведением — один тест из класса репрезентативен.',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    introducedIn: 'S04',
    related: ['bva'],
    examTrap:
      'Практик прогоняет по партиции много значений «на всякий случай». Канон: для покрытия класса эквивалентности достаточно ОДНОГО значения из него — остальные считаются эквивалентными и нового дефекта по определению не дадут. Вопрос на минимум тест-кейсов ждёт «по одному на каждый валидный и невалидный класс».',
  },
  {
    id: 'bva',
    termRu: 'Boundary Value Analysis',
    termEn: 'Boundary Value Analysis (BVA)',
    short: 'Тестирование значений на границах EP-классов. Off-by-one bugs.',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    introducedIn: 'S04',
    related: ['ep'],
    examTrap:
      'На практике обычно берут «границу и соседей» на глаз. Канон 4.0 различает ДВЕ равноправные версии: 2-value (граница + ближайший сосед соседнего класса) и 3-value (граница + оба соседа). В вопросе экзамена всегда указано, какую применить — считай точки покрытия именно её.',
  },
  {
    id: 'decision-table',
    termRu: 'Таблица решений',
    termEn: 'Decision Table',
    short: 'Таблица «условия × действия» для покрытия комбинаций бизнес-правил.',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    introducedIn: 'S04',
    examTrap:
      'Соблазн — оставить только «реальные» комбинации. Канон полной таблицы решений требует все комбинации условий, затем их сворачивают. Минимальное покрытие = по одному тесту на каждый столбец (правило), а не на каждое условие. Дистрактор «тест на условие» путает условия со столбцами.',
  },
  {
    id: 'risk-based-testing',
    termRu: 'Risk-based testing',
    termEn: 'Risk-based testing',
    short: 'Приоритизация тестов по риску. Risk = Probability × Impact.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
  },
  {
    id: 'smoke',
    termRu: 'Smoke testing',
    termEn: 'Smoke testing',
    short: 'Базовая проверка после билда — «работает ли вообще». 5–10 мин.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['sanity', 'regression'],
    examTrap:
      'В силлабусе 4.0 smoke мелькает дважды мимоходом (пример entry-критерия и квадрант Q4) — как термин не определяется и не экзаменуется; sanity нет вовсе. Вопросов «smoke vs sanity» на экзамене не будет; в ответах про levels/types используй канон: component/integration/system/acceptance · functional/non-functional.',
  },
  {
    id: 'sanity',
    termRu: 'Sanity testing',
    termEn: 'Sanity testing',
    short: 'Узкая проверка конкретной фичи после фикса. 15–30 мин.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['smoke', 'regression'],
    examTrap:
      'В силлабусе 4.0 sanity не существует — на собесе различие smoke/sanity спросят, на экзамене ISTQB нет. Каноничная пара для «проверки после фикса» — confirmation testing (re-testing) + regression testing.',
  },
  {
    id: 'regression',
    termRu: 'Regression testing',
    termEn: 'Regression testing',
    short: 'Повторное прогонение тестов после изменений — проверяем, что старое не сломалось.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['smoke', 'sanity'],
    examTrap:
      'В жизни «прогнать регресс» значит «прогнать всё». Канон точнее: confirmation testing (re-testing) подтверждает сам фикс, regression — что фикс не сломал НЕИЗМЕНЁННЫЕ части. На экзамене это два разных ответа — читай, про что вопрос.',
  },
  {
    id: 'pesticide-paradox',
    termRu: 'Парадокс пестицида',
    termEn: 'Pesticide Paradox',
    short: 'Принцип №5 ISTQB: повторяющиеся тесты перестают находить новые дефекты.',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    introducedIn: 'S01',
    examTrap:
      'Расхожая трактовка — «тесты со временем изнашиваются». Канон точнее: одни и те же тесты, повторяясь, перестают находить НОВЫЕ дефекты — нужно пересматривать и добавлять тест-кейсы. Это про обновление набора тестов, а не про «протухание» самих тестов или баг-фикс.',
  },
  {
    id: 'ac',
    termRu: 'Acceptance Criteria',
    termEn: 'Acceptance Criteria (AC)',
    short: 'Условия, при которых user story считается выполненной. Должны быть testable.',
    extended: 'Часто формируются в формате Given-When-Then (BDD).',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['gwt', 'dod'],
  },
  {
    id: 'gwt',
    termRu: 'Given-When-Then',
    termEn: 'Given-When-Then (GWT)',
    short: 'BDD-формат AC: начальное состояние · действие · результат.',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['ac'],
  },
  {
    id: 'dod',
    termRu: 'Definition of Done',
    termEn: 'Definition of Done (DoD)',
    short: 'Команда определяет, при каких условиях тикет считается сделанным. Часто включает code review + тесты + AC.',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    introducedIn: 'S01',
    related: ['ac'],
  },
  {
    id: 'unit-testing',
    termRu: 'Модульное тестирование',
    termEn: 'Unit Testing',
    short: 'Уровень тестирования отдельных модулей / функций. Пишут разработчики.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
  },
  {
    id: 'integration-testing',
    termRu: 'Интеграционное тестирование',
    termEn: 'Integration Testing',
    short: 'Уровень тестирования взаимодействия модулей.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
    examTrap:
      '«Интеграционное» в речи — любое «не юнит». Канон различает ДВА уровня: component integration (взаимодействие модулей внутри системы) и system integration (взаимодействие с внешними системами/сервисами). Вопрос про тестирование стыка с платёжным API ждёт system integration, а не «просто интеграционное».',
  },
  {
    id: 'system-testing',
    termRu: 'Системное тестирование',
    termEn: 'System Testing',
    short: 'Тестирование всего приложения как единого целого.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
  },
  {
    id: 'acceptance-testing',
    termRu: 'Приёмочное тестирование',
    termEn: 'Acceptance Testing',
    short: 'Тестирование клиентом / Product / бизнес-стороной перед релизом.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
    examTrap:
      'Практик думает «приёмку делают тестировщики перед релизом». Канон: UAT/приёмочное проводят ЗАКАЗЧИК и бизнес-пользователи, цель — уверенность и готовность к эксплуатации, а не поиск дефектов. Формы: UAT, operational (OAT), contractual, regulatory, alpha/beta. Дистрактор «находит максимум багов» — не цель приёмки.',
  },
  {
    id: 'black-box',
    termRu: 'Black Box',
    termEn: 'Black Box Testing',
    short: 'Тестирование без знания внутренней структуры — видишь только UI / спецификацию.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['white-box', 'grey-box'],
    examTrap:
      '«Чёрный ящик = тестируют вслепую» — миф. Канон: техники на основе спецификации (EP, BVA, таблица решений, диаграмма состояний) системны и измеримы покрытием требований, без знания кода. Вопрос относит EP/BVA именно к black-box, а покрытие операторов/ветвей — к white-box.',
  },
  {
    id: 'white-box',
    termRu: 'White Box',
    termEn: 'White Box Testing',
    short: 'Тестирование со знанием кода. Code review · path coverage.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['black-box', 'grey-box'],
    examTrap:
      'Опасная интуиция: «100% покрытие кода = нет багов». Канон: покрытие ветвей (branch) включает покрытие операторов (statement) — 100% branch гарантирует 100% statement, но не наоборот; и никакое покрытие не доказывает отсутствие дефектов (принцип «тестирование показывает наличие, а не отсутствие»).',
  },
  {
    id: 'grey-box',
    termRu: 'Grey Box',
    termEn: 'Grey Box Testing',
    short: 'Частичное знание (например, схема БД).',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['black-box', 'white-box'],
  },
  {
    id: 'pre-conditions',
    termRu: 'Pre-conditions',
    termEn: 'Pre-conditions',
    short: 'Условия, которые должны быть верны ДО шагов воспроизведения. Критичны для воспроизводимости.',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    introducedIn: 'S01',
  },
  {
    id: 'sm2',
    termRu: 'SM-2 (Spaced Repetition)',
    termEn: 'SuperMemo 2 algorithm',
    short: 'Алгоритм интервального повторения для flashcards. Адаптивный — увеличивает интервал по recall.',
    istqb: 'CTFL-Ch1',
    introducedIn: 'S06',
  },
  {
    id: 'mock-exam',
    termRu: 'Mock exam',
    termEn: 'Mock exam',
    short: 'Тренировочный экзамен · симулирует реальный CTFL формат (40 Q · 60 мин).',
    istqb: 'CTFL-Ch1',
    introducedIn: 'S06',
  },
  {
    id: 'k-level',
    termRu: 'K-level (Bloom\'s Taxonomy)',
    termEn: 'K-level (Bloom\'s Taxonomy)',
    short: 'K1 = recognition · K2 = comprehension · K3 = application · K4 = analysis. ISTQB CTFL покрывает K1-K3.',
    istqb: 'CTFL-Ch1',
    introducedIn: 'S06',
  },
  // — Ch.2 Lifecycle additions —
  {
    id: 'v-model',
    termRu: 'V-модель',
    termEn: 'V-Model',
    short: 'Sequential SDLC: каждому уровню разработки соответствует уровень тестирования (unit ↔ component, system ↔ system test).',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    introducedIn: 'S01',
    related: ['unit-testing', 'integration-testing', 'system-testing'],
  },
  {
    id: 'spiral-model',
    termRu: 'Spiral · спиральная модель',
    termEn: 'Spiral Model',
    short: 'Итеративная модель (Boehm, 1986): каждый виток фокусируется на самых рискованных частях проекта. Самая дорогая из классических моделей — много времени уходит на анализ рисков и прототипы.',
    extended: 'Виток = 4 квадранта: цели и ограничения → анализ рисков и прототип → разработка и тест → план следующего витка. Применяется в долгих дорогих проектах с высокой неопределённостью: космос, оборона, исследовательские разработки.',
    introducedIn: 'S01',
    related: ['iterative-incremental-model', 'waterfall', 'sdlc'],
  },
  {
    id: 'traceability',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    termRu: 'Прослеживаемость',
    termEn: 'Traceability',
    short: 'Возможность проследить связь: требование → тест-кейс → баг. Обязательна в регулируемых отраслях (медицина, автопром) — там, где работает V-Model.',
    introducedIn: 'S01',
    related: ['rtm', 'v-model'],
  },
  {
    id: 'framework',
    termRu: 'Фреймворк',
    termEn: 'Framework',
    short: 'Готовый «каркас» правил и практик, по которому работает команда (Scrum, Kanban) или пишется код (Selenium). Берёшь каркас — наполняешь своим.',
    introducedIn: 'S01',
    related: ['scrum', 'kanban', 'agile'],
  },
  {
    id: 'scrumban',
    termRu: 'Scrumban',
    termEn: 'Scrumban',
    short: 'Гибрид: Scrum для плановых фич + Kanban для потока багов и инцидентов. Частый выбор в больших компаниях.',
    introducedIn: 'S01',
    related: ['scrum', 'kanban'],
  },
  {
    id: 'legacy',
    termRu: 'Legacy · легаси',
    termEn: 'Legacy code',
    short: 'Старый код, который страшно менять: тестов мало, авторы давно ушли. Для QA — постоянный источник регрессий.',
    introducedIn: 'S01',
    related: ['regression'],
  },
  {
    id: 'prompt',
    termRu: 'Промпт',
    termEn: 'Prompt',
    short: 'Запрос к AI (Claude / ChatGPT), сформулированный текстом. Чем конкретнее — роль, формат ответа, ограничения — тем полезнее результат. Готовые шаблоны промптов — в тикете про AI каждого спринта.',
    introducedIn: 'S01',
    related: ['anti-pattern'],
  },
  {
    id: 'anti-pattern',
    termRu: 'Антипаттерн',
    termEn: 'Anti-pattern',
    short: 'Типовая ошибка, замаскированная под решение: так делают часто, но это создаёт проблемы. Пример из S01: «AI, напиши за меня тест-кейсы» — антипаттерн использования AI.',
    introducedIn: 'S01',
    related: ['prompt'],
  },
  {
    id: 'shift-left',
    termRu: 'Shift-left',
    termEn: 'Shift-left testing',
    short: 'Раннее вовлечение QA: тестирование требований и дизайна, а не только продукта.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['static-testing', 'qa'],
    examTrap:
      'Модное слово из вакансий. За ним стоит канон-принцип «раннее тестирование» (early testing, testing saves money): ревью и тест-активности начинают как можно раньше в SDLC, потому что дефект дешевле найти в требованиях, чем в проде. На экзамене ждут связь с принципом, а не buzzword.',
  },
  {
    id: 'functional-testing',
    termRu: 'Функциональное тестирование',
    termEn: 'Functional testing',
    short: 'Тип тестирования: «что» делает система. Соответствует требованиям функциональности.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['non-functional-testing'],
  },
  {
    id: 'non-functional-testing',
    termRu: 'Нефункциональное тестирование',
    termEn: 'Non-functional testing',
    short: '«Как» работает система: производительность, безопасность, usability, надёжность.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S03',
    related: ['performance-testing', 'security-testing', 'usability-testing'],
  },
  {
    id: 'performance-testing',
    termRu: 'Тестирование производительности',
    termEn: 'Performance testing',
    short: 'Измерение отклика, throughput, ресурсов под нагрузкой. Включает load, stress, endurance.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['non-functional-testing'],
  },
  {
    id: 'security-testing',
    termRu: 'Тестирование безопасности',
    termEn: 'Security testing',
    short: 'Поиск уязвимостей: auth, data leaks, injection, XSS, CSRF.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['non-functional-testing'],
  },
  {
    id: 'usability-testing',
    termRu: 'Тестирование удобства',
    termEn: 'Usability testing',
    short: 'Насколько легко пользователь достигает цели. Включает A/B, eye-tracking, think-aloud.',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['non-functional-testing'],
  },
  // — Ch.3 Static testing additions —
  {
    id: 'review',
    termRu: 'Ревью',
    termEn: 'Review',
    short: 'Общий термин для статической оценки артефакта группой. Включает walkthrough, technical review, inspection.',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    introducedIn: 'S02',
    related: ['walkthrough', 'inspection', 'technical-review'],
  },
  {
    id: 'walkthrough',
    termRu: 'Walkthrough',
    termEn: 'Walkthrough',
    short: 'Неформальное ревью: автор «проводит» команду по артефакту, собирает обратную связь.',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    introducedIn: 'S02',
    related: ['review', 'inspection'],
  },
  {
    id: 'inspection',
    termRu: 'Инспекция',
    termEn: 'Inspection',
    short: 'Самый формальный вид ревью: модератор, чек-лист, метрики, документирование дефектов.',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    introducedIn: 'S02',
    related: ['review', 'walkthrough'],
    examTrap:
      'В жизни «инспекция» = «посмотреть код». Канон: inspection — самый ФОРМАЛЬНЫЙ вид ревью, ведёт обученный модератор (НЕ автор), по правилам и чек-листам, с ролями и сбором метрик. Автор не ведёт инспекцию. Шкала формальности: informal review, walkthrough, technical review, inspection.',
  },
  {
    id: 'technical-review',
    termRu: 'Technical review',
    termEn: 'Technical review',
    short: 'Полу-формальное ревью технических артефактов peer-группой. Цель — техническое качество.',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    introducedIn: 'S02',
    related: ['review'],
  },
  // — Ch.4 Test design additions —
  {
    id: 'state-transition',
    termRu: 'Тестирование переходов состояний',
    termEn: 'State Transition Testing',
    short: 'Black-box техника: states, events, transitions. Покрытие: 0-switch, 1-switch.',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    introducedIn: 'S04',
    related: ['black-box'],
  },
  {
    id: 'use-case-testing',
    termRu: 'Use case testing',
    termEn: 'Use Case Testing',
    short: 'Тесты по сценариям использования: actor, main flow, alternative flows, exceptions.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S04',
  },
  {
    id: 'exploratory-testing',
    termRu: 'Исследовательское тестирование',
    termEn: 'Exploratory Testing',
    short: 'Опытная техника без скриптов: одновременно проектируешь, выполняешь и учишься. Time-boxed.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S04',
    related: ['error-guessing'],
    examTrap:
      '«Исследовательское = бессистемно потыкать» — неверно. Канон: это структурированный подход (часто session-based с charter и таймбоксом), где проектирование, выполнение и оценка идут одновременно; результат документируется. Относится к experience-based техникам, а не к «тестированию без подготовки».',
  },
  {
    id: 'error-guessing',
    termRu: 'Угадывание ошибок',
    termEn: 'Error Guessing',
    short: 'Опытная техника: используя интуицию и опыт, придумываешь, где могут быть баги.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S04',
    related: ['exploratory-testing'],
    examTrap:
      'Звучит как «угадал баг наугад». Канон: error guessing — полноценная experience-based техника: тестировщик опирается на ОПЫТ о типичных ошибках, прошлых дефектах и слабых местах продукта. Это знание, а не случайность — потому и стоит в одном ряду с exploratory и checklist-based.',
  },
  {
    id: 'checklist-based',
    termRu: 'Тестирование по чек-листу',
    termEn: 'Checklist-based Testing',
    short: 'Опытная техника: тесты по подготовленному списку проверок. Compromise между скриптами и exploratory.',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    introducedIn: 'S04',
  },
  // — Ch.5 Test Management additions (was severely under-represented) —
  {
    id: 'test-plan',
    termRu: 'Test Plan',
    termEn: 'Test Plan',
    short: 'Документ: scope, подходы, ресурсы, расписание тестирования. IEEE 829 standard.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S02',
    related: ['test-strategy', 'entry-criteria', 'exit-criteria'],
  },
  {
    id: 'test-strategy',
    termRu: 'Test Strategy',
    termEn: 'Test Strategy',
    short: 'Высокоуровневое описание подходов к тестированию на уровне организации/проекта.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['test-plan'],
    examTrap:
      'Практик зовёт «стратегией» план на спринт. Канон разводит уровни: test strategy — обобщённое, организационно-уровневое описание ПОДХОДА (часто на несколько проектов), а test plan — конкретика ПОД проект (объём, расписание, ресурсы, критерии). Стратегия отвечает «как в принципе тестируем», план — «что/когда/кто здесь».',
  },
  {
    id: 'entry-criteria',
    termRu: 'Entry criteria',
    termEn: 'Entry Criteria',
    short: 'Условия начала тестирования: готовые требования, окружение, билд проходит smoke.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['exit-criteria', 'test-plan'],
  },
  {
    id: 'exit-criteria',
    termRu: 'Exit criteria',
    termEn: 'Exit Criteria',
    short: 'Условия завершения: % покрытия, открытые баги по severity, всё regression прошло.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['entry-criteria', 'test-plan'],
  },
  {
    id: 'defect-management',
    termRu: 'Defect Management',
    termEn: 'Defect Management',
    short: 'Процесс: открытие → triage → fix → verification → close. Часто — Jira/Bugzilla workflow.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['severity', 'priority'],
  },
  {
    id: 'test-monitoring',
    termRu: 'Test monitoring',
    termEn: 'Test Monitoring & Control',
    short: 'Сбор метрик во время выполнения и корректировка плана при отклонениях.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['test-metrics'],
  },
  {
    id: 'test-metrics',
    termRu: 'Метрики тестирования',
    termEn: 'Test Metrics',
    short: 'Числа: defect density, test execution rate, defect leakage, coverage %.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
    related: ['test-monitoring'],
  },
  {
    id: 'test-closure',
    termRu: 'Test Closure',
    termEn: 'Test Closure',
    short: 'Финальная активность: lessons learned, передача артефактов, отчёт стейкхолдерам.',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    introducedIn: 'S05',
  },
  // — Ch.6 Tools (was empty) —
  {
    id: 'test-management-tool',
    termRu: 'Test management tool',
    termEn: 'Test management tool',
    short: 'Хранит требования, тест-кейсы, прогоны, баги. Примеры: TestRail, Zephyr, qTest, Xray.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S05',
  },
  {
    id: 'defect-tracker',
    termRu: 'Defect tracking tool',
    termEn: 'Defect tracking tool',
    short: 'Учёт дефектов с workflow и историей. Примеры: Jira, Bugzilla, Redmine, Linear.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S05',
    related: ['defect-management'],
  },
  {
    id: 'test-automation-tool',
    termRu: 'Tool автоматизации',
    termEn: 'Test automation tool',
    short: 'Запускает скрипты против UI/API. Примеры: Selenium, Cypress, Playwright, Appium.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S06',
    examTrap:
      'Бизнес ждёт «автоматизация = быстрее и дешевле сразу». Канон трезв: инструменты ускоряют ВЫПОЛНЕНИЕ и регресс, но несут риски — затраты на поддержку, недооценку ROI, чрезмерные ожидания, что «инструмент сам тестирует». Автоматизация не заменяет проектирование тестов и не улучшает их качество. Вопрос часто проверяет ЗНАНИЕ рисков, а не выгод.',
  },
  {
    id: 'ci-cd-pipeline',
    termRu: 'CI/CD',
    termEn: 'Continuous Integration / Delivery',
    short: 'Автоматический pipeline: сборка → тесты → деплой. Tools: Jenkins, GitHub Actions, GitLab CI.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S06',
    related: ['test-automation-tool'],
  },
  {
    id: 'api-testing-tool',
    termRu: 'API testing tool',
    termEn: 'API testing tool',
    short: 'Отправляет HTTP запросы и валидирует ответы. Примеры: Postman, Insomnia, RestAssured, Karate.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S06',
  },
  {
    id: 'performance-tool',
    termRu: 'Performance tool',
    termEn: 'Performance testing tool',
    short: 'Симулирует нагрузку и собирает метрики. Примеры: JMeter, Gatling, k6, LoadRunner.',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    introducedIn: 'S06',
    related: ['performance-testing'],
  },
  {
    id: 'actual-result',
    termRu: 'фактический результат',
    termEn: 'Actual Result',
    short: 'Что реально произошло при выполнении тест-шага. Сравнивается с Expected Result в Bug Report',
    introducedIn: 'S04',
  },
  {
    id: 'agile',
    termRu: 'гибкая методология',
    termEn: 'Agile',
    short: 'Семейство итеративных подходов: маленькие циклы, частая обратная связь, гибкость к изменениям',
    introducedIn: 'S01',
  },
  {
    id: 'alpha-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'альфа-тестирование',
    termEn: 'Alpha Testing',
    short: 'Acceptance, проводимый внутри организации-разработчика на типичных юзер-сценариях, до выхода на ограниченный круг внешних пользователей',
    introducedIn: 'S06',
  },
  {
    id: 'audit',
    termRu: 'аудит',
    termEn: 'Audit',
    short: 'Самая формальная форма review: внешняя независимая проверка соответствия документа стандарту/процессу. К2. новый в S06.',
    introducedIn: 'S06',
  },
  {
    id: 'author',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'автор',
    termEn: 'Author',
    short: 'Создатель артефакта (требований, кода, документа), отвечающий за rework по найденным дефектам review',
    introducedIn: 'S06',
  },
  {
    id: 'backlog',
    termRu: 'бэклог',
    termEn: 'Backlog',
    short: 'Приоритизированный список задач команды или продукта. Содержит User Stories, баги, technical debt. Управляется PO',
    introducedIn: 'S01',
  },
  {
    id: 'beta-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'бета-тестирование',
    termEn: 'Beta Testing',
    short: 'Acceptance после Alpha: ограниченный круг внешних пользователей тестирует продукт в реальных условиях до общего релиза',
    introducedIn: 'S06',
  },
  {
    id: 'black-box-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование чёрного ящика',
    termEn: 'Black-box Testing',
    short: 'Без доступа к коду: вход → результат. Техники EP, BVA, Decision Table',
    introducedIn: 'S03',
  },
  {
    id: 'branch-coverage',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    termRu: 'покрытие ветвлений',
    termEn: 'Branch Coverage',
    short: 'White-box метрика: % ветвлений (if/else, switch), пройденных тестами. 100% — каждая ветка хотя бы раз',
    introducedIn: 'S05',
  },
  {
    id: 'brd',
    termRu: 'документ бизнес-требований',
    termEn: 'BRD (Business Requirements Document)',
    short: 'Формальный документ с целями, scope, ограничениями, стейкхолдерами. Чаще в корпорациях, не в стартапах',
    introducedIn: 'S02',
  },
  {
    id: 'bug-report',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'баг-репорт',
    termEn: 'Bug Report (Defect Report)',
    short: 'Документ дефекта: title, шаги, expected vs actual, severity, priority, окружение, прикрепления',
    introducedIn: 'S04',
  },
  {
    id: 'bug-reopen-rate',
    termRu: 'процент переоткрытий бага',
    termEn: 'Bug Reopen Rate',
    short: 'Метрика: % багов, вернувшихся из Fixed в Reopened. Высокий — плохие фиксы или нечёткие репорты',
    introducedIn: 'S05',
  },
  {
    id: 'bug-workflow',
    termRu: 'жизненный цикл бага',
    termEn: 'Bug Workflow',
    short: 'New → Triage → In Progress → Fixed → Ready for Re-test → Verified → Closed (+Reopened/Won\'t Fix)',
    introducedIn: 'S04',
  },
  {
    id: 'checklist',
    termRu: 'чек-лист',
    termEn: 'Checklist',
    short: 'Лёгкая форма тест-документации: список проверок без детальных шагов. Для smoke и regression-минимума',
    introducedIn: 'S04',
  },
  {
    id: 'cicd-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент непрерывной интеграции/доставки',
    termEn: 'CI/CD Tool',
    short: 'Автоматизация pipeline: build → test → deploy. GitHub Actions, GitLab CI, Jenkins, CircleCI',
    introducedIn: 'S06',
  },
  {
    id: 'code-review',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    termRu: 'код-ревью',
    termEn: 'Code Review',
    short: 'Процесс, когда другой разработчик читает код перед мерджем, ищет ошибки и предлагает улучшения',
    introducedIn: 'S01',
  },
  {
    id: 'compatibility-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'тестирование совместимости',
    termEn: 'Compatibility Testing',
    short: 'Работа в разных браузерах/ОС/устройствах. BrowserStack, LambdaTest. Подвид non-functional',
    introducedIn: 'S03',
  },
  {
    id: 'component-integration-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование интеграции компонентов',
    termEn: 'Component Integration Testing',
    short: 'Проверка взаимодействия модулей внутри одной системы (внутренние API, общие данные). Уровень: между Unit и System',
    introducedIn: 'S06',
  },
  {
    id: 'component-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'компонентное (юнит) тестирование',
    termEn: 'Component Testing (Unit Testing)',
    short: 'Тестирование отдельного модуля в изоляции. Самый низкий уровень пирамиды. Пишут разработчики',
    introducedIn: 'S03',
  },
  {
    id: 'contract-acceptance-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'приёмка по контракту',
    termEn: 'Contract Acceptance Testing',
    short: 'UAT-вариант: проверка соответствия системы контрактным обязательствам перед заказчиком (часто госзаказ)',
    introducedIn: 'S06',
  },
  {
    id: 'coverage-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент покрытия кода',
    termEn: 'Coverage Tool',
    short: 'Измеряет % строк/ветвлений/путей, выполненных тестами. JaCoCo, Coverage.py, Istanbul. Часто intrusive',
    introducedIn: 'S06',
  },
  {
    id: 'decision-table-testing',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    termRu: 'тестирование по таблице решений',
    termEn: 'Decision Table Testing',
    short: 'Таблица комбинаций условий и результатов. Покрывает валидные комбинации входов. Для бизнес-правил',
    introducedIn: 'S05',
  },
  {
    id: 'defect',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'дефект',
    termEn: 'Defect',
    short: 'ISTQB-цепочка: error (человек) → defect (код/документ) → failure (наблюдение). В индустрии всё «баг»',
    introducedIn: 'S04',
  },
  {
    id: 'defect-density',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'плотность дефектов',
    termEn: 'Defect Density',
    short: 'Количество багов на 1000 строк кода (KLOC). Качество кода по модулям',
    introducedIn: 'S05',
  },
  {
    id: 'defect-detection-percentage',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'процент обнаружения дефектов',
    termEn: 'Defect Detection Percentage (DDP)',
    short: '% багов, найденных до релиза от общего числа. Цель ≥95%',
    introducedIn: 'S05',
  },
  {
    id: 'defect-leakage',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'утечка дефектов',
    termEn: 'Defect Leakage',
    short: '% багов в production от общего числа. Зеркало DDP. Цель &lt;5%',
    introducedIn: 'S05',
  },
  {
    id: 'defect-tracking-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент трекинга дефектов',
    termEn: 'Defect Tracking Tool',
    short: 'Хранение, статусы, история, метрики багов. Jira, Bugzilla, Mantis, Linear',
    introducedIn: 'S06',
  },
  {
    id: 'definition-of-ready',
    termRu: 'критерий готовности к работе',
    termEn: 'Definition of Ready (DoR)',
    short: 'Чек-лист: задача готова к разработке. Требования, AC, дизайн, зависимости',
    introducedIn: 'S02',
  },
  {
    id: 'devops-ci-cd',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'DevOps',
    termEn: 'DevOps / CI-CD',
    short: 'Практика, при которой разработка и эксплуатация слиты: pipeline, автотесты в CI, мониторинг в проде',
    introducedIn: 'S01',
  },
  {
    id: 'end-to-end-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'сквозное тестирование',
    termEn: 'End-to-End Testing (E2E)',
    short: 'Полный пользовательский сценарий от логина до подтверждения. Имитация реального юзера',
    introducedIn: 'S03',
  },
  {
    id: 'edge-case',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'граничный случай',
    termEn: 'Edge Case',
    short: 'Граничная или нетипичная ситуация: пустая строка, MAX_INT, null, потеря сети. Часто источник багов',
    introducedIn: 'S03',
  },
  {
    id: 'error',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'ошибка человека',
    termEn: 'Error',
    short: 'Действие человека, приводящее к defect: опечатка, неверная логика в требовании, ошибка дизайна',
    introducedIn: 'S06',
    examTrap:
      'В жизни всё это зовут одним словом «баг». На экзамене цепочка строгая: error (человек ошибся) → defect (изъян в артефакте/коде) → failure (наблюдаемый сбой). Слова «bug» в каноне нет; вопросы любят проверять направление цепочки.',
  },
  {
    id: 'expected-result',
    termRu: 'ожидаемый результат',
    termEn: 'Expected Result',
    short: 'Что должно произойти по спеке. Обязательное поле в TC и Bug Report',
    introducedIn: 'S04',
  },
  {
    id: 'experience-based-techniques',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'техники на основе опыта',
    termEn: 'Experience-based Techniques',
    short: 'Семейство: Exploratory, Error Guessing, Checklist-based. Не заменяют формальные, дополняют',
    introducedIn: 'S05',
  },
  {
    id: 'failure',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'сбой / отказ системы',
    termEn: 'Failure',
    short: 'Наблюдаемое неправильное поведение системы (что юзер видит). Возникает из defect, который из error',
    introducedIn: 'S04',
    examTrap:
      'Практики говорят «нашёл баг в коде» — экзамен так не скажет. Failure — это только НАБЛЮДАЕМОЕ поведение; то, что в коде, — defect. И помни: не каждый defect приводит к failure (мёртвый код), и failure бывает без defect (среда, железо).',
  },
  {
    id: 'functional-requirements',
    termRu: 'функциональные требования',
    termEn: 'Functional Requirements',
    short: 'Что система должна делать: «корзина считает скидку». Основной фокус QA',
    introducedIn: 'S02',
  },
  {
    id: 'functional-testing-2',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'функциональное тестирование',
    termEn: 'Functional Testing',
    short: 'Проверка ЧТО система делает по требованиям. «Работает ли фича так, как описано»',
    introducedIn: 'S03',
  },
  {
    id: 'gherkin-given-when-then',
    termRu: 'Gherkin / Дано-Когда-Тогда',
    termEn: 'Gherkin / Given-When-Then',
    short: 'Формат сценария: предусловие → действие → результат. BDD и AC',
    introducedIn: 'S02',
  },
  {
    id: 'grey-box-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование серого ящика',
    termEn: 'Grey-box Testing',
    short: 'Комбинация Black и White: код не читаем, но знаем архитектуру/API/БД. Самый частый подход junior QA',
    introducedIn: 'S03',
  },
  {
    id: 'happy-path',
    istqb: 'CTFL-Ch4',
    klevel: 'K1',
    termRu: 'основной сценарий',
    termEn: 'Happy Path',
    short: 'Позитивный путь: данные валидны, юзер по плану, система отвечает корректно. Базовый кейс',
    introducedIn: 'S03',
  },
  {
    id: 'hotfix',
    termRu: 'хотфикс',
    termEn: 'Hotfix',
    short: 'Срочное исправление критбага в production. Вне обычного релизного цикла. Обязательная regression после',
    introducedIn: 'S01',
  },
  {
    id: 'informal-review',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'неформальный review',
    termEn: 'Informal Review',
    short: 'Самая лёгкая форма static testing: автор показывает документ коллеге, тот даёт комментарии. Без процесса, без отчёта',
    introducedIn: 'S06',
  },
  {
    id: 'intrusive-vs-non-intrusive-tools',
    istqb: 'CTFL-Ch6',
    klevel: 'K2',
    termRu: 'внедряемые vs не-внедряемые инструменты',
    termEn: 'Intrusive vs Non-intrusive Tools',
    short: 'Intrusive влияет на SUT (coverage instrumenters); non-intrusive не влияет (HTTP-снифер, log analyzer)',
    introducedIn: 'S06',
  },
  {
    id: 'iterative-incremental-model',
    termRu: 'итеративная модель',
    termEn: 'Iterative / Incremental Model',
    short: 'Между Waterfall и Agile: итерации проходят полный цикл (анализ → код → тест). Регрессия растёт',
    introducedIn: 'S01',
  },
  {
    id: 'k-level-2',
    termRu: 'уровень когнитивной сложности',
    termEn: 'K-level (K1 / K2 / K3)',
    short: '',
    introducedIn: 'S06',
  },
  {
    id: 'kanban',
    termRu: 'канбан',
    termEn: 'Kanban',
    short: 'Управление через доску с WIP-лимитами, без спринтов. Задачи текут потоком',
    introducedIn: 'S01',
  },
  {
    id: 'maintenance-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование при сопровождении',
    termEn: 'Maintenance Testing',
    short: 'Тестирование изменений после релиза: фиксы, миграции, обновления окружения. Включает re-testing и regression',
    introducedIn: 'S03',
  },
  {
    id: 'moderator',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'модератор',
    termEn: 'Moderator',
    short: 'Ведущий formal Inspection: готовит артефакт, проверяет entry/exit criteria, фиксирует exit-метрики. К2. новый в S06 (Ch.3.2).',
    introducedIn: 'S06',
  },
  {
    id: 'moscow',
    termRu: 'приоритизация MoSCoW',
    termEn: 'MoSCoW',
    short: 'Must / Should / Could / Won\'t have (this time). Делит scope на «без этого нет релиза» и «без этого можно»',
    introducedIn: 'S02',
  },
  {
    id: 'negative-testing',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'негативное тестирование',
    termEn: 'Negative Testing',
    short: 'Невалидные данные: пустые поля, спецсимволы, SQL injection. Корректность обработки ошибок',
    introducedIn: 'S03',
  },
  {
    id: 'non-functional-requirements',
    termRu: 'нефункциональные требования',
    termEn: 'Non-functional Requirements (NFR)',
    short: 'Как система работает: скорость, нагрузка, безопасность. «Страница &lt;2 сек»',
    introducedIn: 'S02',
  },
  {
    id: 'non-functional-testing-2',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'нефункциональное тестирование',
    termEn: 'Non-functional Testing',
    short: 'Performance, Security, Usability, Compatibility. Проверка КАК работает, не ЧТО делает',
    introducedIn: 'S03',
  },
  {
    id: 'open-source-vs-commercial-tools',
    istqb: 'CTFL-Ch6',
    klevel: 'K2',
    termRu: 'open-source vs коммерческие инструменты',
    termEn: 'Open-source vs Commercial Tools',
    short: 'Open-source: бесплатно, кастомизация, community-поддержка (Selenium). Commercial: цена, зрелая поддержка, integrations (LoadRunner)',
    introducedIn: 'S06',
  },
  {
    id: 'operational-acceptance-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'операционная приёмка',
    termEn: 'Operational Acceptance Testing (OAT)',
    short: 'Подвид Acceptance: backup/restore, recovery, security, install. Для production-environments',
    introducedIn: 'S06',
  },
  {
    id: 'pairwise-testing',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'парное тестирование',
    termEn: 'Pairwise Testing',
    short: 'Комбинаторная техника: покрытие всех пар параметров вместо всех комбинаций. Сокращает количество тестов',
    introducedIn: 'S05',
  },
  {
    id: 'pass-rate',
    termRu: 'процент прохождения',
    termEn: 'Pass Rate',
    short: '% пройденных тест-кейсов от общего выполненных. Базовый индикатор готовности к релизу',
    introducedIn: 'S05',
  },
  {
    id: 'performance-testing-2',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'тестирование производительности',
    termEn: 'Performance Testing',
    short: 'Скорость, отзывчивость, стабильность под нагрузкой. Подвиды: Load, Stress, Soak, Spike',
    introducedIn: 'S03',
  },
  {
    id: 'performance-tool-2',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент performance-тестирования',
    termEn: 'Performance Tool',
    short: 'Симуляция нагрузки и измерение метрик. JMeter, k6, Gatling, LoadRunner',
    introducedIn: 'S06',
  },
  {
    id: 'pm',
    termRu: 'менеджер проекта',
    termEn: 'PM (Project Manager)',
    short: 'Сроки, ресурсы, коммуникация со стейкхолдерами. В Scrum часто отсутствует',
    introducedIn: 'S01',
  },
  {
    id: 'po',
    termRu: 'владелец продукта',
    termEn: 'PO (Product Owner)',
    short: '«Что делать»: приоритет фич, бэклог, бизнес-цели. Одна из трёх ролей Scrum',
    introducedIn: 'S01',
  },
  {
    id: 'pr',
    termRu: 'pull request',
    termEn: 'PR (Pull Request)',
    short: 'Предложение слить ветку с изменениями в основную. Перед мерджем — code review и CI-тесты',
    introducedIn: 'S01',
  },
  {
    id: 'prd',
    termRu: 'документ продуктовых требований',
    termEn: 'PRD (Product Requirements Document)',
    short: 'От Product Manager: бизнес-цели, User Stories, scope. Отправная точка тестирования',
    introducedIn: 'S02',
  },
  {
    id: 'qa-2',
    termRu: 'обеспечение качества',
    termEn: 'QA (Quality Assurance)',
    short: 'Процессы предотвращения дефектов: code review, ревью требований, командные стандарты. Включает QC и Testing',
    introducedIn: 'S01',
  },
  {
    id: 'qc-2',
    termRu: 'контроль качества',
    termEn: 'QC (Quality Control)',
    short: 'Контроль готового продукта: нахождение существующих дефектов через инспекции и Testing',
    introducedIn: 'S01',
  },
  {
    id: 'quality',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'качество',
    termEn: 'Quality',
    short: 'Степень, в которой продукт удовлетворяет нужды стейкхолдеров. Многомерное: функциональность, надёжность, производительность, удобство и др',
    introducedIn: 'S06',
  },
  {
    id: 'quality-attribute',
    termRu: 'атрибут качества',
    termEn: 'Quality Attribute (FURPS / ISO 25010)',
    short: 'Измеримое свойство: Functionality, Usability, Reliability, Performance, Security, Maintainability и др. (ISO 25010 — 8 категорий)',
    introducedIn: 'S06',
  },
  {
    id: 're-testing',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    termRu: 'повторное тестирование (подтверждение)',
    termEn: 'Re-testing (Confirmation Testing)',
    short: 'Повтор того же кейса, который раньше падал, после фикса. Подтверждает закрытие бага. ≠ regression',
    introducedIn: 'S04',
  },
  {
    id: 'regression-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'регрессионное тестирование',
    termEn: 'Regression Testing',
    short: 'Повтор тестов после изменений: новый фикс не сломал ранее работавшие фичи',
    introducedIn: 'S01',
  },
  {
    id: 'regulatory-acceptance-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'приёмка по нормативам',
    termEn: 'Regulatory Acceptance Testing',
    short: 'Acceptance вариант: соответствие законам/регуляторам (HIPAA, GDPR, PCI DSS). Часто mandatory',
    introducedIn: 'S06',
  },
  {
    id: 'requirement',
    termRu: 'требование',
    termEn: 'Requirement',
    short: 'Описание того, что система должна делать или каким свойством обладать. Business / functional / non-functional / UI-UX',
    introducedIn: 'S02',
  },
  {
    id: 'review-meeting',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'встреча review',
    termEn: 'Review Meeting',
    short: 'Этап review: совместный разбор найденных дефектов, классификация, фиксация в Review Report',
    introducedIn: 'S06',
  },
  {
    id: 'review-report',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'отчёт review',
    termEn: 'Review Report',
    short: 'Документ с найденными issues, классификацией, exit-criteria status и решением (rework / accept)',
    introducedIn: 'S06',
  },
  {
    id: 'reviewer',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'reviewer (рецензент)',
    termEn: 'Reviewer',
    short: 'Участник review: индивидуально готовится и приходит с комментариями. Не модератор и не автор',
    introducedIn: 'S06',
  },
  {
    id: 'risk',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'риск',
    termEn: 'Risk',
    short: 'Потенциальная проблема: вероятность × impact. Product risk (баг в проде) vs project risk (срыв сроков)',
    introducedIn: 'S06',
  },
  {
    id: 'risk-analysis',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'анализ рисков',
    termEn: 'Risk Analysis',
    short: 'Идентификация + оценка рисков. Результат — приоритизация по критичности модулей для глубины тестирования',
    introducedIn: 'S06',
  },
  {
    id: 'risk-based-testing-2',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'тестирование на основе рисков',
    termEn: 'Risk-Based Testing',
    short: 'Приоритизация: критичные модули глубже, низкорисковые поверхностно. Риск = вероятность × impact',
    introducedIn: 'S03',
  },
  {
    id: 'sanity-testing',
    termRu: 'санити-тестирование',
    termEn: 'Sanity Testing',
    short: 'Узкая проверка конкретной фичи или фикса. В индустрии часто синоним smoke; ISTQB различает',
    introducedIn: 'S03',
  },
  {
    id: 'scribe',
    istqb: 'CTFL-Ch3',
    klevel: 'K1',
    termRu: 'писарь review',
    termEn: 'Scribe',
    short: 'Отдельная роль на formal Inspection: ведёт лог дефектов в реальном времени для exit-протокола',
    introducedIn: 'S06',
  },
  {
    id: 'scrum',
    termRu: 'Scrum',
    termEn: 'Scrum',
    short: 'Agile-фреймворк: спринты 1-4 недели, три роли (PO, SM, Dev), четыре церемонии (Planning, Daily, Review, Retro)',
    introducedIn: 'S01',
  },
  {
    id: 'scrum-master',
    termRu: 'Scrum Master',
    termEn: 'Scrum Master',
    short: 'Защищает команду от внешнего шума, убирает блокеры. Не «начальник» — фасилитатор',
    introducedIn: 'S01',
  },
  {
    id: 'sdlc',
    termRu: 'жизненный цикл разработки',
    termEn: 'SDLC (Software Development Life Cycle)',
    short: 'Requirements → Design → Development → Testing → Release → Maintenance. 5 моделей: Waterfall / V-Model / Iterative / Agile / DevOps',
    introducedIn: 'S01',
  },
  {
    id: 'security-testing-2',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'тестирование безопасности',
    termEn: 'Security Testing',
    short: 'Устойчивость к атакам: SQL injection, XSS, broken auth. OWASP Top 10. Подвид non-functional',
    introducedIn: 'S03',
  },
  {
    id: 'shift-left-testing',
    istqb: 'CTFL-Ch1',
    klevel: 'K2',
    termRu: 'сдвиг тестирования влево',
    termEn: 'Shift-left Testing',
    short: 'Тестирование начинается рано: ревью требований, AC до Dev. ISTQB Principle 3 «Early Testing Saves Money»',
    introducedIn: 'S01',
  },
  {
    id: 'sign-off',
    termRu: 'sign-off',
    termEn: 'Sign-off',
    short: 'Одобрение QA на релиз: «протестировано, можно катить». Завершает цикл тестирования',
    introducedIn: 'S01',
  },
  {
    id: 'smoke-testing',
    termRu: 'smoke-тестирование',
    termEn: 'Smoke Testing',
    short: 'Быстрая поверхностная проверка после деплоя: «приложение вообще работает?». До полного цикла тестирования',
    introducedIn: 'S03',
  },
  {
    id: 'sprint',
    termRu: 'спринт',
    termEn: 'Sprint',
    short: 'Фиксированный период работы в Scrum (1-4 недели). Planning → Daily → Review → Retro',
    introducedIn: 'S01',
  },
  {
    id: 'stakeholder',
    termRu: 'стейкхолдер',
    termEn: 'Stakeholder',
    short: 'Заинтересованная сторона: PO, заказчик, юзер, юристы, маркетинг. У каждого свои требования',
    introducedIn: 'S02',
  },
  {
    id: 'statement-coverage',
    istqb: 'CTFL-Ch4',
    klevel: 'K3',
    termRu: 'покрытие операторов',
    termEn: 'Statement Coverage',
    short: 'White-box метрика: % строк кода, выполненных тестами. 100% — каждая строка хотя бы раз. Слабее branch',
    introducedIn: 'S05',
  },
  {
    id: 'static-analysis-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент статического анализа',
    termEn: 'Static Analysis Tool',
    short: 'Автоматическая проверка кода без запуска: code smells, complexity, security issues. SonarQube, ESLint, Checkstyle',
    introducedIn: 'S06',
  },
  {
    id: 'story-points',
    termRu: 'story points',
    termEn: 'Story Points',
    short: 'Относительная оценка сложности User Story: 1, 2, 3, 5, 8, 13 (Фибоначчи). Команда оценивает на Planning. Не часы',
    introducedIn: 'S02',
  },
  {
    id: 'sut',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'система под тестом',
    termEn: 'SUT (System Under Test)',
    short: 'Объект тестирования: приложение, сервис, модуль. Что мы тестируем «снаружи»',
    introducedIn: 'S06',
  },
  {
    id: 'system-integration-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование интеграции систем',
    termEn: 'System Integration Testing',
    short: 'Уровень после Component Integration: проверка взаимодействия между внешними системами/сервисами через API/middleware',
    introducedIn: 'S06',
  },
  {
    id: 'tech-spec',
    termRu: 'техническая спецификация',
    termEn: 'Tech Spec (Technical Specification)',
    short: 'От Tech Lead: API, структура БД, взаимодействие сервисов. Для интеграционных тестов и edge cases',
    introducedIn: 'S02',
  },
  {
    id: 'technical-review-2',
    istqb: 'CTFL-Ch3',
    klevel: 'K2',
    termRu: 'технический review',
    termEn: 'Technical Review',
    short: 'Среднеформальный static testing: peer-review кода или дизайна; есть процесс, но менее строго чем Inspection',
    introducedIn: 'S06',
  },
  {
    id: 'test-approach',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'тестовый подход',
    termEn: 'Test Approach',
    short: 'Реализация Test Strategy на уровне проекта: какие техники, инструменты, среды, кто за что отвечает',
    introducedIn: 'S06',
  },
  {
    id: 'test-automation-tool-2',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент автоматизации тестирования',
    termEn: 'Test Automation Tool',
    short: 'Автоматический запуск тестов: Selenium, Playwright, Cypress, Postman/Newman, Appium',
    introducedIn: 'S06',
  },
  {
    id: 'test-basis',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'основа тестирования',
    termEn: 'Test Basis',
    short: 'Источники для дизайна тестов: требования, AC, дизайн, код, опыт. Откуда «знаем», что тестировать',
    introducedIn: 'S06',
  },
  {
    id: 'test-case',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'тест-кейс',
    termEn: 'Test Case (TC)',
    short: 'Детальный тест: ID, предусловие, шаги, данные, expected result, priority. Основная единица',
    introducedIn: 'S04',
  },
  {
    id: 'test-charter',
    termRu: 'тест-чартер',
    termEn: 'Test Charter',
    short: 'Цель exploratory-сессии в 1-2 предложениях: scope, фокус, тайминг. «Оплата на мобилке, edge cases, 60 мин»',
    introducedIn: 'S04',
  },
  {
    id: 'test-completion-report',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'отчёт о завершении тестирования',
    termEn: 'Test Completion Report',
    short: 'Финальный документ: что сделано, что не сделано, метрики, lessons learned, рекомендация о готовности к релизу',
    introducedIn: 'S06',
  },
  {
    id: 'test-condition',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'тестовое условие',
    termEn: 'Test Condition',
    short: 'Аспект SUT, проверяемый одним или несколькими тест-кейсами. Например: «корзина считает скидку 10% для авторизованных юзеров»',
    introducedIn: 'S06',
  },
  {
    id: 'test-control',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'управление тестированием',
    termEn: 'Test Control',
    short: 'Корректирующие действия по результатам Test Monitoring: добавить ресурсы, пересмотреть scope, изменить approach',
    introducedIn: 'S06',
  },
  {
    id: 'test-coverage',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'покрытие тестами',
    termEn: 'Test Coverage',
    short: 'Какая часть требований / кода / функциональности покрыта тестами. Не путать с code coverage',
    introducedIn: 'S05',
  },
  {
    id: 'test-cycle',
    termRu: 'тестовый цикл',
    termEn: 'Test Cycle',
    short: 'Один полный прогон тест-сьюта по конкретному билду. Включает все запуски и статусы',
    introducedIn: 'S06',
  },
  {
    id: 'test-data',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'тестовые данные',
    termEn: 'Test Data',
    short: 'Входные данные для выполнения тестов: валидные, невалидные, граничные, синтетические. Faker, Mockaroo',
    introducedIn: 'S06',
  },
  {
    id: 'test-design-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент дизайна тестов',
    termEn: 'Test Design Tool',
    short: 'Помощь в создании test data, генерация комбинаций (decision tables, pairwise)',
    introducedIn: 'S06',
  },
  {
    id: 'test-design-techniques',
    istqb: 'CTFL-Ch4',
    klevel: 'K2',
    termRu: 'техники тест-дизайна',
    termEn: 'Test Design Techniques',
    short: 'Методы выбора кейсов: Black-box (EP, BVA, Decision Table, State, Pairwise), White-box, Experience-based',
    introducedIn: 'S05',
  },
  {
    id: 'test-environment',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'тестовое окружение',
    termEn: 'Test Environment',
    short: 'Hardware + software + network + data, на которых выполняются тесты. Dev, Staging, UAT, Pre-prod',
    introducedIn: 'S06',
  },
  {
    id: 'test-estimation',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'оценка тестирования',
    termEn: 'Test Estimation',
    short: 'Прогноз effort/времени/ресурсов на тестирование scope. Story Points, экспертные оценки, исторические данные',
    introducedIn: 'S06',
  },
  {
    id: 'test-execution-tool',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент выполнения тестов',
    termEn: 'Test Execution Tool',
    short: 'Автоматический запуск тестов и фиксация результатов. Selenium, Playwright, JUnit, Postman/Newman',
    introducedIn: 'S06',
  },
  {
    id: 'test-levels',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'уровни тестирования',
    termEn: 'Test Levels',
    short: 'Иерархия по scope: Component → Component Integration → System → System Integration → Acceptance',
    introducedIn: 'S06',
  },
  {
    id: 'test-management',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'управление тестированием',
    termEn: 'Test Management',
    short: 'Деятельность по планированию, мониторингу и контролю тестирования. Включает Plan, Estimation, Schedule, Monitoring, Control, Completion',
    introducedIn: 'S06',
  },
  {
    id: 'test-management-tool-2',
    istqb: 'CTFL-Ch6',
    klevel: 'K1',
    termRu: 'инструмент тест-менеджмента',
    termEn: 'Test Management Tool',
    short: 'Хранение тест-кейсов, прогонов, метрик, отчётности. TestRail, Zephyr, qase.io, Xray',
    introducedIn: 'S04',
  },
  {
    id: 'test-monitoring-2',
    istqb: 'CTFL-Ch5',
    klevel: 'K2',
    termRu: 'мониторинг тестирования',
    termEn: 'Test Monitoring',
    short: 'Сбор данных о ходе: % прогона, найденные баги, blocker\'ы, статус exit criteria',
    introducedIn: 'S06',
  },
  {
    id: 'test-object',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'объект тестирования',
    termEn: 'Test Object',
    short: 'Конкретный артефакт, который тестируется: компонент, сервис, документ требований. Часть SUT',
    introducedIn: 'S06',
  },
  {
    id: 'test-objective',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'цель тестирования',
    termEn: 'Test Objective (Goal)',
    short: 'Зачем тестируем: найти дефекты / снизить риск / соответствовать стандарту / получить confidence. К1. новый в S06 (Ch.5.1).',
    introducedIn: 'S06',
  },
  {
    id: 'test-oracle',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'тест-оракул',
    termEn: 'Test Oracle',
    short: 'Источник, который определяет, правильный ли получился результат. Спецификация / другая система / эксперт / здравый смысл',
    introducedIn: 'S06',
  },
  {
    id: 'test-progress-report',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'отчёт о ходе тестирования',
    termEn: 'Test Progress Report',
    short: 'Промежуточный отчёт: статус прогона, найденные баги, риски, escalation. Daily / weekly cadence',
    introducedIn: 'S06',
  },
  {
    id: 'test-run',
    termRu: 'прогон',
    termEn: 'Test Run',
    short: 'Один запуск конкретного тест-кейса в конкретной среде с конкретным билдом. Имеет статус (Pass/Fail/Blocked/Skipped)',
    introducedIn: 'S06',
  },
  {
    id: 'test-scenario',
    termRu: 'тест-сценарий',
    termEn: 'Test Scenario',
    short: 'Высокоуровневое ЧТО тестировать: «Проверить регистрацию». Один Scenario = несколько Test Cases',
    introducedIn: 'S04',
  },
  {
    id: 'test-schedule',
    istqb: 'CTFL-Ch5',
    klevel: 'K1',
    termRu: 'расписание тестирования',
    termEn: 'Test Schedule',
    short: 'Тайминг: когда какие тесты, milestones, демо, release candidate, exit criteria checkpoints',
    introducedIn: 'S06',
  },
  {
    id: 'test-suite',
    termRu: 'тест-сьют',
    termEn: 'Test Suite',
    short: 'Группа связанных тест-кейсов, запускаемых вместе. «Smoke Suite Checkout» = 12 кейсов',
    introducedIn: 'S04',
  },
  {
    id: 'test-type',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'тип тестирования',
    termEn: 'Test Type',
    short: 'Группировка по цели: functional, non-functional, white-box, change-related (re-test/regression)',
    introducedIn: 'S06',
  },
  {
    id: 'testing',
    istqb: 'CTFL-Ch1',
    klevel: 'K1',
    termRu: 'тестирование',
    termEn: 'Testing',
    short: 'Конкретное действие: спроектировать тест → выполнить → сравнить ожидаемый и фактический результат. Подмножество QC',
    introducedIn: 'S01',
  },
  {
    id: 'bug',
    termRu: 'баг',
    termEn: 'Bug',
    short: 'Ошибка в продукте: он ведёт себя не так, как ожидается. В индустрии «баг» = дефект.',
    introducedIn: 'S01',
    related: ['defect', 'bug-report'],
  },
  {
    id: 'tester',
    termRu: 'тестировщик',
    termEn: 'Tester',
    short: 'Тот, кто ищет проблемы раньше пользователя: открывает продукт, пробует сценарии и находит, что сломано.',
    introducedIn: 'S01',
    related: ['testing', 'qc'],
  },
  {
    id: 'domain',
    termRu: 'домен',
    termEn: 'Domain',
    short: 'Область, которую тестируешь: web, mobile, игры, финтех, медтех и другие. У каждой свои особенности и риски.',
    introducedIn: 'S01',
  },
  {
    id: 'track',
    termRu: 'трек',
    termEn: 'Track',
    short: 'Направление роста в профессии: ручное тестирование, автоматизация, SDET.',
    introducedIn: 'S01',
  },
  {
    id: 'ai-buddy',
    termRu: 'AI-напарник',
    termEn: 'AI Buddy',
    short: 'AI-инструмент-помощник: предлагает варианты и черновики, а решение принимаешь ты.',
    introducedIn: 'S01',
    related: ['prompt'],
  },
  {
    id: 'boundaries',
    termRu: 'края (границы)',
    termEn: 'Boundaries',
    short: 'Крайние значения, где продукт чаще ломается: пусто, ноль, максимум, отрицательное.',
    introducedIn: 'S01',
    related: ['edge-case', 'bva'],
  },
  {
    id: 'uat',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'приёмочное тестирование пользователем',
    termEn: 'UAT (User Acceptance Testing)',
    short: 'Конечные пользователи проверяют систему до релиза. Самый частый вид Acceptance в продуктовых командах',
    introducedIn: 'S06',
  },
  {
    id: 'usability-testing-2',
    istqb: 'CTFL-Ch2',
    klevel: 'K1',
    termRu: 'тестирование удобства использования',
    termEn: 'Usability Testing',
    short: 'Удобство для конечного юзера: learnability, efficiency, errors, satisfaction. Эвристики Нильсена',
    introducedIn: 'S03',
  },
  {
    id: 'use-case',
    termRu: 'use case',
    termEn: 'Use Case',
    short: 'Формат требования: actor → шаги → цель + альтернативы + исключения. Шире User Story по scope',
    introducedIn: 'S04',
  },
  {
    id: 'user-story',
    termRu: 'user story',
    termEn: 'User Story',
    short: '«As a [role], I want [action], so that [benefit]». Главная единица работы Agile-спринта',
    introducedIn: 'S02',
  },
  {
    id: 'verification-vs-validation',
    termRu: 'верификация vs валидация',
    termEn: 'Verification vs Validation',
    short: 'Verification = «делаем правильно» (по спеке). Validation = «делаем правильный продукт» (для юзера). Классическая путаница на собесе',
    introducedIn: 'S03',
  },
  {
    id: 'waterfall',
    termRu: 'Waterfall',
    termEn: 'Waterfall',
    short: 'Линейная модель SDLC: каждая фаза заканчивается «приёмкой», возврат назад дорог. Стабильные требования',
    introducedIn: 'S01',
  },
  {
    id: 'white-box-testing',
    istqb: 'CTFL-Ch2',
    klevel: 'K2',
    termRu: 'тестирование белого ящика',
    termEn: 'White-box Testing',
    short: 'С доступом к коду: ветвления, циклы, покрытие. Обычно пишут разработчики',
    introducedIn: 'S03',
  },
  {
    id: 'wip',
    termRu: 'задачи в работе одновременно',
    termEn: 'WIP (Work In Progress)',
    short: 'WIP-лимит ограничивает количество, чтобы команда не распылялась. Базовый механизм Kanban',
    introducedIn: 'S01',
  },
];

/**
 * Полный глоссарий курса = Block 1 (S01-S05) + Block 2 (S07+). Используется для
 * тултипов, поиска и страницы глоссария — там нужны ВСЕ термины. CTFL-тренажёр
 * (Flashcards.tsx) берёт только BLOCK_1_TERMS — см. комментарий выше.
 */
export const GLOSSARY: ReadonlyArray<GlossaryTerm> = [
  ...BLOCK_1_TERMS,
  ...BLOCK_2_TERMS,
  ...BLOCK_3_MOBILE_TERMS,
  ...BLOCK_3_TERMS,
];

export const glossaryById = (id: string): GlossaryTerm | undefined => GLOSSARY.find((g) => g.id === id);

/** Phase 2 search · simple substring · upgrades to Fuse.js fuzzy search in Phase 3 (per A.5.2). */
export const searchGlossary = (query: string): GlossaryTerm[] => {
  if (!query.trim()) return GLOSSARY.slice();
  const q = query.toLowerCase();
  return GLOSSARY.filter(
    (g) =>
      g.termRu.toLowerCase().includes(q) ||
      g.termEn.toLowerCase().includes(q) ||
      g.short.toLowerCase().includes(q) ||
      g.id.includes(q),
  );
};
